import React from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';

const FinanceLoanBook = React.lazy(() => import('./FinanceLoanBook'));
const FinanceLoanDetail = React.lazy(() => import('./FinanceLoanDetail'));
const FinanceTrends = React.lazy(() => import('./FinanceTrends'));
const FinanceStatements = React.lazy(() => import('./FinanceStatements'));
const FinanceImport = React.lazy(() => import('./FinanceImport'));

/**
 * Finance — One Finance's loan book (27 Oct 2026, Neil's finance brief at Bulimba).
 * A standalone module: its own backend (`business-builder-backend/src/modules/finance/`), its own
 * permissions (FINANCE_VIEW / _EDIT / _ADMIN), its own kit. It links OUT to Deal Tracker deals
 * (command) only where the server says the viewer may open them.
 *
 * `id` MUST equal the backend permissions moduleId ('finance') or the navbar silently hides it.
 */
const financeModuleConfig: ModuleConfig = {
  id: 'finance',
  name: 'Finance',
  description: "One Finance's loan book — every loan from the monthly statements, matched to its deal, with the book value and a six-month forecast",
  version: '0.1.0',
  enabled: true,
  category: 'business',
  order: 21,
  icon: '🏦',

  routes: [
    { path: '/loan-book', component: FinanceLoanBook, permissionLevel: 'view', useLayout: true, layoutOptions: { maxW: '100%' } },
    { path: '/loan-book/loans/:id', component: FinanceLoanDetail, permissionLevel: 'view', useLayout: true, layoutOptions: { maxW: 'container.xl' } },
    { path: '/loan-book/trends', component: FinanceTrends, permissionLevel: 'view', useLayout: true, layoutOptions: { maxW: 'container.xl' } },
    { path: '/loan-book/statements', component: FinanceStatements, permissionLevel: 'view', useLayout: true, layoutOptions: { maxW: 'container.xl' } },
    { path: '/loan-book/import', component: FinanceImport, permissionLevel: 'admin', useLayout: true, layoutOptions: { maxW: 'container.lg' } },
  ],

  navigation: [
    { label: 'Loan Book', path: '/loan-book', icon: '🏦', permissionLevel: 'view' },
    { label: 'Book & Forecast', path: '/loan-book/trends', icon: '📈', permissionLevel: 'view' },
    { label: 'Loan Statements', path: '/loan-book/statements', icon: '🧾', permissionLevel: 'view' },
    { label: 'Import Statement', path: '/loan-book/import', icon: '📥', permissionLevel: 'admin' },
  ],

  // Replaced at runtime by ModulePermissionsContext (backend SSOT).
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  quickActions: [],

  changelog: [
    {
      version: '0.1.0',
      releaseDate: '2026-10-27',
      title: 'The One Finance loan book',
      features: [
        '🏦 Every loan from the Finsure, WLTH and Allstate statements in one table — book value, last commission, broker, lender',
        '🔗 Each loan matched to its Deal Tracker deal by the client\'s name; anything uncertain waits for a person to confirm',
        '✍️ When-written details (rate, amount, lender, purpose) filled in on the loan itself',
        '📈 Commission month by month and the next six months of trail',
      ],
    },
  ],
};

export default financeModuleConfig;
